import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// Enhanced AnimatedSection with more animation options
const AnimatedSection = ({ 
  children, 
  className = '', 
  delay = 0,
  direction = 'up',
  duration = 0.7,
  scale = false,
  blur = false,
  stagger = false
}) => {
  const getInitialPosition = () => {
    const base = { opacity: 0 };
    
    if (scale) base.scale = 0.95;
    if (blur) base.filter = 'blur(10px)';
    
    switch (direction) {
      case 'left':
        return { ...base, x: -60 };
      case 'right':
        return { ...base, x: 60 };
      case 'down':
        return { ...base, y: -60 };
      case 'up':
      default:
        return { ...base, y: 60 };
    }
  };

  const getFinalPosition = () => {
    const base = { opacity: 1 };
    
    if (scale) base.scale = 1;
    if (blur) base.filter = 'blur(0px)';
    
    switch (direction) {
      case 'left':
      case 'right':
        return { ...base, x: 0 };
      case 'down':
      case 'up':
      default:
        return { ...base, y: 0 };
    }
  };

  return (
    <motion.div
      initial={getInitialPosition()}
      whileInView={getFinalPosition()}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ 
        duration: duration, 
        delay: delay, 
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Section Divider with animated gradient line
export const SectionDivider = ({ variant = 'default' }) => {
  const variants = {
    default: 'from-transparent via-emerald/30 to-transparent',
    gold: 'from-transparent via-gold/30 to-transparent',
    dark: 'from-transparent via-darkgreen/30 to-transparent',
    light: 'from-transparent via-gray-200 to-transparent',
  };

  return (
    <motion.div
      initial={{ scaleX: 0, opacity: 0 }}
      whileInView={{ scaleX: 1, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
      className="w-full py-1"
    >
      <div className={`h-px w-full bg-gradient-to-r ${variants[variant]}`} />
    </motion.div>
  );
};

// Parallax wrapper for subtle depth effect
export const ParallaxWrapper = ({ children, className = '', speed = 0.5 }) => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, speed * 100]);

  return (
    <motion.div style={{ y }} className={className}>
      {children}
    </motion.div>
  );
};

// Staggered children container
export const StaggerContainer = ({ children, className = '', staggerDelay = 0.1 }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: 0.1,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Staggered child item
export const StaggerItem = ({ children, className = '', direction = 'up' }) => {
  const getVariants = () => {
    const hidden = { opacity: 0 };
    const visible = { opacity: 1, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } };

    switch (direction) {
      case 'left':
        hidden.x = -40;
        visible.x = 0;
        break;
      case 'right':
        hidden.x = 40;
        visible.x = 0;
        break;
      case 'down':
        hidden.y = -40;
        visible.y = 0;
        break;
      case 'up':
      default:
        hidden.y = 40;
        visible.y = 0;
    }

    return { hidden, visible };
  };

  return (
    <motion.div variants={getVariants()} className={className}>
      {children}
    </motion.div>
  );
};

// Fade section transition overlay
export const SectionTransition = ({ fromColor = 'white', toColor = 'transparent', height = '120px' }) => {
  return (
    <div 
      className="w-full pointer-events-none relative z-10"
      style={{ 
        height,
        background: `linear-gradient(180deg, ${fromColor} 0%, ${toColor} 100%)`
      }}
    />
  );
};

export default AnimatedSection;
